'use client';

import { getCallDiagnosticsSync } from '@/services/logger/call-diagnostics';
import { createLogger } from '@/services/logger/logger';
import { tracingService } from '@/services/tracing/tracing-service';
import { CallEventLevel, CallEventName } from '@/services/tracing/types';
import type {
  CallEventPayload,
  DeviceCallEndedBy,
  DeviceCallFailedStage,
} from '@/services/tracing/types';
import { type CancelReason, HangupReason } from '@vonage/client-sdk';
import React, { use, useCallback, useEffect } from 'react';
import { setVonageActiveCallId } from '../vonage-client-provider';

const log = createLogger('VoiceCallContainer', ['calls']);

export type VoiceCallStateStatus =
  | 'idle'
  | 'ringing'
  | 'in-progress'
  | 'reconnectable'
  | 'initiating'
  | 'hangingup'
  | 'transferring';

export type VoiceCallContext = {
  /**
   * Number format E.164 of the contact.
   */
  to: string;
  /**
   * Number format E.164 of the origin number (line)
   */
  origin_number: string;
} & Record<string, any>;

export type VoiceContact = {
  id?: string;
  /**
   * The phone number of the contact.
   */
  phoneNumber?: string;
  /**
   * The name of the contact.
   */
  name?: string;
  /**
   * The last extracted name of the contact.
   */
  extractedName?: string;

  companyName?: string;
  jobTitle?: string;
};

export type VoiceContactExtra = {
  summary?: string;
  external_data?: {
    [key: string]: {
      [key: string]: unknown;
    };
  };
};

export type VoiceContactUserLine = {
  lineName?: string;
  lineNumber?: string;
  originalToNumber?: string;
  originalToName?: string;
};

interface SecondCallState {
  id?: string;
  from?: {
    name: string;
    channel: string;
  };
  contact?: VoiceContact;
  extra?: VoiceContactExtra;
  userLine?: VoiceContactUserLine;
}

export type HangupVerificationStatus = 'none' | 'pending' | 'stuck';

export type HangupVerificationSource = 'user' | 'second_call' | 'session_error';

export type HangupVerification = {
  status: HangupVerificationStatus;
  source?: HangupVerificationSource;
  attempts: number;
  startedAt?: number;
  lastError?: string;
  lastLegStatus?: string;
};

const DEFAULT_HANGUP_VERIFICATION: HangupVerification = {
  status: 'none',
  attempts: 0,
};

interface VoiceCallState {
  callState: VoiceCallStateStatus;
  callId?: string;
  direction: 'incoming' | 'outgoing' | 'unknown';
  callCtx?: VoiceCallContext;
  from?: {
    name: string;
    channel: string;
  };
  muted: boolean;
  earmuffed: boolean;
  reason?: CancelReason | HangupReason | 'LOCAL_ERROR';
  contact?: VoiceContact;
  extra?: VoiceContactExtra;
  userLine?: VoiceContactUserLine;
  secondCall?: SecondCallState;
  hasSecondIncomingCall: boolean;
  hangupVerification: HangupVerification;
}

type CallInitiatingAction = {
  type: 'call:initiating';
  callCtx?: VoiceCallContext;
  contact: VoiceContact;
  extra?: VoiceContactExtra;
};

type CallHangingupAction = {
  type: 'call:hangingup';
};

type CallInitiatedAction = {
  type: 'call:initiated';
  callId: string;
  callCtx?: VoiceCallContext;
  contact: VoiceContact;
  extra?: VoiceContactExtra;
};

type CallInviteAction = {
  type: 'call:invite';
  callId: string;
  from: {
    name: string;
    channel: string;
  };
  contact: VoiceContact;
  extra?: VoiceContactExtra;
};

type CallInviteCancelAction = {
  type: 'call:invite:cancel';
  reason: CancelReason;
};

type CallInviteAnsweredAction = {
  type: 'call:invite:answered';
};

type CallInviteRejectedAction = {
  type: 'call:invite:rejected';
  rejectedCallId: string;
};

type CallReconnectableAction = {
  type: 'call:reconnectable';
  callId: string;
  callCtx?: VoiceCallContext;
  from: {
    name: string;
    channel: string;
  };
  contact: VoiceContact;
  extra?: VoiceContactExtra;
  direction: 'incoming' | 'outgoing';
};

type CallReconnectAction = {
  type: 'call:reconnect';
};

type CallMuteUpdatedAction = {
  type: 'call:mute:updated';
  muted: boolean;
};

type CallEarmuffUpdatedAction = {
  type: 'call:earmuff:updated';
  earmuffed: boolean;
};

type CallHangupAction = {
  type: 'call:hangup';
  reason: HangupReason | 'LOCAL_ERROR' | 'AnsweredElsewhere';
  /** When provided, only clear state if this matches the current callId */
  callId?: string;
  /** Caller-supplied origin for the hangup, surfaced as `hangupSource` in tracing. */
  source?: string;
  /** Caller-supplied error context, surfaced as `errorCode` / `errorMessage` in tracing. */
  error?: {
    code?: string;
    message?: string;
  };
};

type CallHangupVerificationStartAction = {
  type: 'call:hangup:verification:start';
  source: HangupVerificationSource;
};

type CallHangupVerificationProgressAction = {
  type: 'call:hangup:verification:progress';
  attempts?: number;
  lastError?: string;
  lastLegStatus?: string;
};

type CallHangupVerificationStuckAction = {
  type: 'call:hangup:verification:stuck';
};

type CallHangupVerifiedAction = {
  type: 'call:hangup:verified';
  reason: HangupReason | 'LOCAL_ERROR';
  /** The callId this verification belongs to — prevents stale verification clearing a new call */
  callId?: string;
};

type CallStateUpdatedAction = {
  type: 'call:state:updated';
  callState: 'in-progress';
  callId: string;
};

type CallContactUpdatedAction = {
  type: 'call:contact:updated';
  contact: VoiceContact;
  extra?: VoiceContactExtra;
  userLine?: VoiceContactUserLine;
};

type CallContactResetAction = {
  type: 'call:contact:reset';
};

type CallSecondAnsweredAction = {
  type: 'call:second:answered';
};

type CallSecondCancelAction = {
  type: 'call:second:cancel';
  reason: CancelReason;
};

type CallSecondContactUpdatedAction = {
  type: 'call:second:contact:updated';
  contact: VoiceContact;
  extra?: VoiceContactExtra;
  userLine?: VoiceContactUserLine;
};

type CallTransferAction = {
  type: 'call:transferring';
};

type CallTransferFailedAction = {
  type: 'call:transfer:failed';
};

type VoiceCallAction =
  | CallInitiatingAction
  | CallInitiatedAction
  | CallInviteAction
  | CallInviteCancelAction
  | CallInviteAnsweredAction
  | CallInviteRejectedAction
  | CallMuteUpdatedAction
  | CallEarmuffUpdatedAction
  | CallHangupAction
  | CallStateUpdatedAction
  | CallReconnectableAction
  | CallReconnectAction
  | CallHangingupAction
  | CallContactUpdatedAction
  | CallContactResetAction
  | CallSecondAnsweredAction
  | CallSecondCancelAction
  | CallSecondContactUpdatedAction
  | CallTransferAction
  | CallTransferFailedAction
  | CallHangupVerificationStartAction
  | CallHangupVerificationProgressAction
  | CallHangupVerificationStuckAction
  | CallHangupVerifiedAction;

const resetSecondCallState = {
  secondCall: undefined,
  hasSecondIncomingCall: false,
};

const VoiceCallReducer: React.Reducer<VoiceCallState, VoiceCallAction> = (state, action) => {
  switch (action.type) {
    case 'call:initiating':
      return {
        callState: 'initiating',
        callCtx: action.callCtx,
        direction: 'outgoing',
        muted: false,
        earmuffed: false,
        contact: action.contact,
        extra: action.extra,
        hasSecondIncomingCall: false,
        hangupVerification: DEFAULT_HANGUP_VERIFICATION,
      };

    case 'call:initiated':
      return {
        callState: 'ringing',
        callId: action.callId,
        callCtx: action.callCtx,
        direction: 'outgoing',
        muted: false,
        earmuffed: false,
        contact: {
          ...state.contact,
          ...(action.contact?.id !== undefined && { id: action.contact.id }),
          ...(action.contact?.phoneNumber !== undefined && {
            phoneNumber: action.contact.phoneNumber,
          }),
          ...(action.contact?.name !== undefined && { name: action.contact.name }),
          ...(action.contact?.extractedName !== undefined && {
            extractedName: action.contact.extractedName,
          }),
          ...(action.contact?.companyName !== undefined && {
            companyName: action.contact.companyName,
          }),
          ...(action.contact?.jobTitle !== undefined && { jobTitle: action.contact.jobTitle }),
        },
        extra: action.extra,
        hasSecondIncomingCall: false,
        hangupVerification: DEFAULT_HANGUP_VERIFICATION,
      };

    case 'call:invite':
      if (state.callState === 'in-progress' || state.callState === 'ringing') {
        return {
          ...state,
          secondCall: {
            id: action.callId,
            from: action.from,
            contact: action.contact,
            extra: action.extra,
            userLine: undefined,
          },
          hasSecondIncomingCall: true,
        };
      }
      return {
        callState: 'ringing',
        callId: action.callId,
        from: action.from,
        direction: 'incoming',
        muted: false,
        earmuffed: false,
        contact: action.contact,
        extra: action.extra,
        userLine: undefined,
        hasSecondIncomingCall: false,
        hangupVerification: DEFAULT_HANGUP_VERIFICATION,
      };

    case 'call:invite:cancel':
      return {
        ...state,
        callState: 'idle',
        callId: undefined,
        callCtx: undefined,
        contact: undefined,
        extra: undefined,
        userLine: undefined,
        reason: action.reason,
        muted: false,
        earmuffed: false,
        direction: 'unknown',
        hangupVerification: DEFAULT_HANGUP_VERIFICATION,
        ...resetSecondCallState,
      };

    case 'call:invite:answered':
      return { ...state, callState: 'in-progress' };

    case 'call:invite:rejected':
      if (state.secondCall?.id === action.rejectedCallId) {
        return {
          ...state,
          ...resetSecondCallState,
        };
      }

      if (state.callId === action.rejectedCallId) {
        return {
          ...state,
          callState: 'idle',
          callId: undefined,
          callCtx: undefined,
          contact: undefined,
          extra: undefined,
          userLine: undefined,
          reason: HangupReason.LocalHangup,
          muted: false,
          earmuffed: false,
          direction: 'unknown',
          hangupVerification: DEFAULT_HANGUP_VERIFICATION,
          ...resetSecondCallState,
        };
      }

      return state;

    case 'call:second:answered':
      return {
        ...state,
        callState: 'in-progress',
        callId: state.secondCall?.id,
        contact: state.secondCall?.contact,
        extra: state.secondCall?.extra,
        userLine: state.secondCall?.userLine,
        from: state.secondCall?.from,
        direction: 'incoming',
        ...resetSecondCallState,
      };

    case 'call:second:cancel': {
      return {
        ...state,
        ...resetSecondCallState,
      };
    }

    case 'call:second:contact:updated':
      return state.secondCall
        ? {
            ...state,
            secondCall: {
              ...state.secondCall,
              contact: action.contact,
              extra: action.extra,
              userLine: action.userLine,
            },
          }
        : state;

    case 'call:reconnectable':
      return {
        callState: 'reconnectable',
        callId: action.callId,
        callCtx: action.callCtx,
        from: action.from,
        direction: action.direction,
        muted: false,
        earmuffed: false,
        contact: action.contact,
        extra: action.extra,
        hasSecondIncomingCall: false,
        hangupVerification: DEFAULT_HANGUP_VERIFICATION,
      };
    case 'call:reconnect':
      return { ...state, callState: 'in-progress' };
    case 'call:mute:updated':
      return { ...state, muted: action.muted };
    case 'call:earmuff:updated':
      return { ...state, earmuffed: action.earmuffed };
    case 'call:hangup':
      // When callId is provided, ignore stale hangups targeting a different call
      if (action.callId && action.callId !== state.callId) {
        return state;
      }
      return {
        ...state,
        callState: 'idle',
        callId: undefined,
        callCtx: undefined,
        contact: undefined,
        extra: undefined,
        userLine: undefined,
        reason: action.reason,
        muted: false,
        earmuffed: false,
        direction: 'unknown',
        hangupVerification: DEFAULT_HANGUP_VERIFICATION,
      };
    case 'call:hangup:verification:start':
      return {
        ...state,
        callState: 'hangingup',
        hangupVerification: {
          status: 'pending',
          source: action.source,
          attempts: 0,
          startedAt: Date.now(),
        },
      };
    case 'call:hangup:verification:progress':
      return {
        ...state,
        hangupVerification: {
          ...state.hangupVerification,
          ...(action.attempts !== undefined && { attempts: action.attempts }),
          ...(action.lastError !== undefined && { lastError: action.lastError }),
          ...(action.lastLegStatus !== undefined && { lastLegStatus: action.lastLegStatus }),
        },
      };
    case 'call:hangup:verification:stuck':
      return {
        ...state,
        hangupVerification: {
          ...state.hangupVerification,
          status: 'stuck',
        },
      };
    case 'call:hangup:verified':
      // Ignore stale verification targeting a different call
      if (action.callId && action.callId !== state.callId) {
        return state;
      }
      // Preserve secondCall state so call:second:answered can promote it
      return {
        ...state,
        callState: 'idle',
        callId: undefined,
        callCtx: undefined,
        contact: undefined,
        extra: undefined,
        userLine: undefined,
        reason: action.reason,
        muted: false,
        earmuffed: false,
        direction: 'unknown',
        hangupVerification: DEFAULT_HANGUP_VERIFICATION,
      };
    case 'call:state:updated':
      if (action.callId !== state.callId) {
        return state;
      }

      if (action.callState === state.callState) {
        return state;
      }

      return { ...state, callState: action.callState };
    case 'call:hangingup':
      return { ...state, callState: 'hangingup' };
    case 'call:contact:updated':
      return {
        ...state,
        contact: action.contact,
        extra: action.extra ?? state.extra,
        userLine: action.userLine ?? state.userLine,
      };
    case 'call:contact:reset':
      return { ...state, contact: undefined, extra: undefined, userLine: undefined };
    case 'call:transferring':
      return { ...state, callState: 'transferring' };
    case 'call:transfer:failed':
      return state.callState === 'transferring' ? { ...state, callState: 'in-progress' } : state;
    default:
      return state;
  }
};

export const VoiceCallContext = React.createContext<
  [VoiceCallState, React.Dispatch<VoiceCallAction>]
>([
  {
    callState: 'idle',
    direction: 'unknown',
    muted: false,
    earmuffed: false,
    hasSecondIncomingCall: false,
    hangupVerification: DEFAULT_HANGUP_VERIFICATION,
  },
  () => {},
]);

type VoiceCallContainerProps = {
  enableLocalCache?: boolean;
  children: React.ReactNode;
};

const ERROR_HANGUP_REASONS: Set<string> = new Set([
  'LOCAL_ERROR',
  'session_error',
  'MEDIA_TIMEOUT',
  'network_error',
  'session_timeout',
]);

const DIRECTION_MAP: Record<string, 'inbound' | 'outbound'> = {
  incoming: 'inbound',
  outgoing: 'outbound',
};

function channelToE164(channel: string | undefined): string | undefined {
  if (!channel) {
    return undefined;
  }
  const phonePrefix = 'phone:';
  if (channel.startsWith(phonePrefix)) {
    const rest = channel.slice(phonePrefix.length);
    return rest.length > 0 ? rest : undefined;
  }
  return undefined;
}

function getCallTraceContext(
  state: VoiceCallState,
  overrides?: { callId?: string },
): CallEventPayload {
  const callId = overrides?.callId ?? state.callId;
  const direction = DIRECTION_MAP[state.direction] ?? undefined;
  const ctx: CallEventPayload = {};
  if (callId) {
    ctx.deviceLegId = callId;
  }
  if (direction) {
    ctx.direction = direction;
  }

  let from: string | undefined;
  let to: string | undefined;
  if (direction === 'outbound') {
    from = state.callCtx?.origin_number;
    to = state.callCtx?.to;
  } else if (direction === 'inbound') {
    from = channelToE164(state.from?.channel);
    to = state.userLine?.lineNumber ?? state.callCtx?.origin_number;
  }
  if (from) {
    ctx.from = from;
  }
  if (to) {
    ctx.to = to;
  }
  return ctx;
}

function stageFromCallState(callState: VoiceCallStateStatus): DeviceCallFailedStage {
  switch (callState) {
    case 'idle':
    case 'initiating':
      return 'call_setup';
    case 'ringing':
      return 'call_ringing';
    case 'in-progress':
    case 'hangingup':
    case 'reconnectable':
    case 'transferring':
      return 'call_in_progress';
    default:
      return 'call_ended';
  }
}

function endedByFromState(state: VoiceCallState, source: string | undefined): DeviceCallEndedBy {
  if (source === 'user') {
    return 'local';
  }
  if (state.callState === 'hangingup') {
    return 'local';
  }
  if (state.hangupVerification.source === 'user') {
    return 'local';
  }
  return 'remote';
}

// Module-level so the entries survive React re-renders / StrictMode double-invokes;
// only the dispatcher writes/reads it.
const answeredAtByCallId = new Map<string, number>();
// Tracks which calls already emitted device_call_failed so the terminal
// device_call_ended is suppressed for the same call (failure and end are
// mutually exclusive in the rollup).
const failedCallIds = new Set<string>();

function markCallFailed(callId: string | undefined): void {
  if (callId) {
    failedCallIds.add(callId);
  }
}

export function trackDeviceCallFailure(
  state: VoiceCallState,
  reason: string,
  errorPayload: Record<string, unknown>,
  options?: { stage?: DeviceCallFailedStage; callId?: string },
): void {
  const ctx = getCallTraceContext(state, { callId: options?.callId });
  tracingService.trackEvent(CallEventName.DeviceCallFailed, CallEventLevel.Error, {
    ...ctx,
    reason,
    stage: options?.stage ?? stageFromCallState(state.callState),
    errorPayload,
  });
  markCallFailed(options?.callId ?? state.callId);
}

function traceVoiceCallAction(action: VoiceCallAction, state: VoiceCallState): void {
  const actionCallId = 'callId' in action ? action.callId : undefined;
  const base = getCallTraceContext(state, { callId: actionCallId ?? state.callId });
  const baseCallId = base.deviceLegId;

  switch (action.type) {
    // Inbound flow
    case 'call:invite':
      tracingService.trackEvent(CallEventName.CallReported, CallEventLevel.Info, base);
      break;
    case 'call:invite:answered':
      tracingService.trackEvent(CallEventName.StatusConnected, CallEventLevel.Info, base);
      if (baseCallId) {
        answeredAtByCallId.set(baseCallId, Date.now());
      }
      break;
    case 'call:invite:cancel':
      tracingService.trackEvent(CallEventName.AttemptCancelled, CallEventLevel.Info, base);
      tracingService.trackEvent(CallEventName.StatusCompleted, CallEventLevel.Info, {
        ...base,
        hangupReason: 'cancelled',
      });
      if (baseCallId) {
        answeredAtByCallId.delete(baseCallId);
        failedCallIds.delete(baseCallId);
      }
      break;
    case 'call:invite:rejected':
      tracingService.trackEvent(CallEventName.AttemptReject, CallEventLevel.Info, base);
      tracingService.trackEvent(CallEventName.StatusCompleted, CallEventLevel.Info, {
        ...base,
        hangupReason: 'rejected',
      });
      if (baseCallId) {
        answeredAtByCallId.delete(baseCallId);
        failedCallIds.delete(baseCallId);
      }
      break;
    // Outbound flow
    case 'call:initiating':
      tracingService.trackEvent(CallEventName.AttemptCall, CallEventLevel.Info, base);
      break;
    case 'call:initiated':
      tracingService.trackEvent(CallEventName.StatusRinging, CallEventLevel.Info, base);
      break;
    // Call status
    case 'call:state:updated':
      if (action.callId === state.callId && action.callState !== state.callState) {
        tracingService.trackEvent(CallEventName.StatusConnected, CallEventLevel.Info, base);
        if (action.callState === 'in-progress' && action.callId) {
          answeredAtByCallId.set(action.callId, Date.now());
        }
      }
      break;
    case 'call:hangup': {
      const reason = String(action.reason);
      const isError = ERROR_HANGUP_REASONS.has(reason);
      const hangupSource = action.source ?? state.hangupVerification.source;
      const wasAnswered = state.callState === 'in-progress' || state.callState === 'hangingup';
      const errorCode = action.error?.code;
      const errorMessage = action.error?.message ?? (isError ? reason : undefined);

      if (isError && state.direction === 'outgoing' && state.callState === 'initiating') {
        tracingService.trackEvent(CallEventName.AttemptCallFailed, CallEventLevel.Error, {
          ...base,
          hangupReason: reason,
          ...(errorCode && { errorCode }),
          ...(errorMessage && { errorMessage }),
          ...(hangupSource && { hangupSource }),
        });
      }

      if (isError) {
        tracingService.trackEvent(CallEventName.DeviceCallFailed, CallEventLevel.Error, {
          ...base,
          reason:
            state.direction === 'outgoing' && state.callState === 'initiating'
              ? CallEventName.AttemptCallFailed
              : reason,
          stage: stageFromCallState(state.callState),
          errorPayload: {
            hangupReason: reason,
            ...(errorCode && { errorCode }),
            ...(errorMessage && { errorMessage }),
            ...(hangupSource && { hangupSource }),
          },
        });
        markCallFailed(baseCallId);
      }

      tracingService.trackEvent(
        CallEventName.StatusCompleted,
        isError ? CallEventLevel.Error : CallEventLevel.Info,
        {
          ...base,
          hangupReason: reason,
          ...(hangupSource && { hangupSource }),
          ...(isError && {
            ...(errorCode && { errorCode }),
            ...(errorMessage && { errorMessage }),
          }),
        },
      );

      if (wasAnswered && baseCallId && !failedCallIds.has(baseCallId)) {
        const answeredAt = answeredAtByCallId.get(baseCallId);
        tracingService.trackEvent(CallEventName.DeviceCallEnded, CallEventLevel.Info, {
          ...base,
          endedBy: endedByFromState(state, action.source),
          ...(answeredAt !== undefined && { durationMs: Date.now() - answeredAt }),
        });
      }
      if (baseCallId) {
        answeredAtByCallId.delete(baseCallId);
        failedCallIds.delete(baseCallId);
      }
      break;
    }
    case 'call:reconnectable':
      tracingService.trackEvent(CallEventName.StatusReconnecting, CallEventLevel.Info, base);
      break;
    // User actions
    case 'call:hangingup':
      tracingService.trackEvent(CallEventName.AttemptHangup, CallEventLevel.Info, base);
      break;
    case 'call:hangup:verified': {
      const verifiedReason = String(action.reason);
      const isVerifiedError = ERROR_HANGUP_REASONS.has(verifiedReason);
      const hangupSource = state.hangupVerification.source;
      const wasAnswered = state.callState === 'in-progress' || state.callState === 'hangingup';

      tracingService.trackEvent(
        CallEventName.StatusCompleted,
        isVerifiedError ? CallEventLevel.Error : CallEventLevel.Info,
        {
          ...base,
          hangupReason: verifiedReason,
          verified: true,
          ...(hangupSource && { hangupSource }),
          ...(isVerifiedError && { errorMessage: verifiedReason }),
        },
      );

      if (isVerifiedError) {
        tracingService.trackEvent(CallEventName.DeviceCallFailed, CallEventLevel.Error, {
          ...base,
          reason: verifiedReason,
          stage: stageFromCallState(state.callState),
          errorPayload: {
            hangupReason: verifiedReason,
            verified: true,
            ...(hangupSource && { hangupSource }),
          },
        });
        markCallFailed(baseCallId);
      }

      if (wasAnswered && baseCallId && !failedCallIds.has(baseCallId)) {
        const answeredAt = answeredAtByCallId.get(baseCallId);
        tracingService.trackEvent(CallEventName.DeviceCallEnded, CallEventLevel.Info, {
          ...base,
          endedBy: endedByFromState(state, undefined),
          ...(answeredAt !== undefined && { durationMs: Date.now() - answeredAt }),
        });
      }
      if (baseCallId) {
        answeredAtByCallId.delete(baseCallId);
        failedCallIds.delete(baseCallId);
      }
      break;
    }
    // Callbacks
    case 'call:mute:updated':
      tracingService.trackEvent(CallEventName.MuteChanged, CallEventLevel.Info, {
        ...base,
        muted: action.muted,
      });
      break;
    case 'call:earmuff:updated':
      tracingService.trackEvent(CallEventName.EarmuffChanged, CallEventLevel.Info, {
        ...base,
        earmuffed: action.earmuffed,
      });
      break;
    case 'call:second:answered': {
      const secondCallId = state.secondCall?.id;
      const secondCtx: CallEventPayload = { direction: 'inbound' };
      if (secondCallId) {
        secondCtx.deviceLegId = secondCallId;
      }
      const secondFrom = channelToE164(state.secondCall?.from?.channel);
      if (secondFrom) {
        secondCtx.from = secondFrom;
      }
      const secondTo = state.secondCall?.userLine?.lineNumber;
      if (secondTo) {
        secondCtx.to = secondTo;
      }
      tracingService.trackEvent(CallEventName.StatusConnected, CallEventLevel.Info, secondCtx);
      if (secondCallId) {
        answeredAtByCallId.set(secondCallId, Date.now());
      }
      break;
    }
    default:
      // Actions without tracing events (contact updates, transfers, etc.)
      break;
  }
}

export const VoiceCallStateProvider = ({
  children,
  enableLocalCache = false,
}: VoiceCallContainerProps) => {
  const [state, baseDispatch] = React.useReducer(VoiceCallReducer, {
    callState: 'idle',
    direction: 'unknown',
    muted: false,
    earmuffed: false,
    hasSecondIncomingCall: false,
    hangupVerification: DEFAULT_HANGUP_VERIFICATION,
  });

  // Mirror of the reducer state, updated synchronously inside `dispatch` so
  // that two dispatches in the same microtask chain (e.g. `call:initiating`
  // immediately followed by `call:hangup` when the SDK throws) see the
  // post-action state instead of the stale pre-render value. Without this,
  // the tracing branch for `call:hangup` would read `callState: 'idle'` and
  // skip `attempt_call_failed`.
  const stateRef = React.useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Mirror the "is a call active?" bit into module-level state so the
  // VonageClientProvider's TTL heartbeat can defer refreshes while a call
  // is in-flight. Setting to undefined on idle lets the next stale-check
  // fire normally.
  useEffect(() => {
    const isActive = state.callState !== 'idle' && !!state.callId;
    setVonageActiveCallId(isActive ? state.callId : undefined);
  }, [state.callState, state.callId]);

  // Unmount-only cleanup. If this provider unmounts while a call is active
  // (error boundary, test teardown, or any future SPA logout that doesn't
  // hard-reload), the mirror effect above would leave the module-level
  // `activeVonageCallId` stuck — and then the heartbeat would defer
  // indefinitely because nothing else clears it until `resetVonageClient`.
  // This is a separate effect (rather than a cleanup on the mirror) so we
  // don't clear+re-set on every reducer transition; the flicker would be
  // harmless but churny under StrictMode's double-invoke.
  useEffect(() => () => setVonageActiveCallId(undefined), []);

  const dispatch = useCallback((action: VoiceCallAction) => {
    const currentState = stateRef.current;
    baseDispatch(action);
    // React commits asynchronously, so the `useEffect` mirror above wouldn't
    // run before a follow-up dispatch in the same microtask. Pre-compute the
    // next state here so tracing reads of `stateRef.current` always reflect
    // the latest action — keeping `attempt_call_failed` reliable when an
    // outgoing call fails before `call:initiated` ever lands.
    stateRef.current = VoiceCallReducer(currentState, action);

    const diagnostics = getCallDiagnosticsSync();
    log.info(`Call action dispatched ${action.type}`, {
      action: action.type,
      currentCallId: currentState.callId,
      currentCallState: currentState.callState,
      callId: 'callId' in action ? action.callId : currentState.callId,
      diagnostics,
    });
    traceVoiceCallAction(action, currentState);
  }, []);

  const reducer: [VoiceCallState, React.Dispatch<VoiceCallAction>] = [state, dispatch];

  const isLocalCacheReconnectable = useCallback(
    (callId: string) => {
      if (!enableLocalCache) {
        return false;
      }
      const localTimestamp = localStorage.getItem(`call:${callId}:timestamp`);
      if (!localTimestamp) {
        return false;
      }

      const localTimestampNumber = Number(localTimestamp);
      const now = Date.now();
      const diff = now - localTimestampNumber;

      return diff < 1000 * 15; // 15 seconds
    },
    [enableLocalCache],
  );

  useEffect(() => {
    if (!enableLocalCache) {
      return;
    }

    const localCallId = localStorage.getItem('call:id');
    if (!localCallId) {
      return;
    }

    if (!isLocalCacheReconnectable(localCallId)) {
      return;
    }

    const localCallCtx = localStorage.getItem(`call:${localCallId}:ctx`);
    const localCallFrom = localStorage.getItem(`call:${localCallId}:from`);
    const localCallDirection = localStorage.getItem(`call:${localCallId}:direction`);
    const localCallContact = localStorage.getItem(`call:${localCallId}:contact`);

    dispatch({
      type: 'call:reconnectable',
      callId: localCallId,
      callCtx: localCallCtx ? JSON.parse(localCallCtx) : undefined,
      from: localCallFrom ? JSON.parse(localCallFrom) : undefined,
      direction: localCallDirection as 'incoming' | 'outgoing',
      contact: localCallContact ? JSON.parse(localCallContact) : undefined,
    });
  }, []);

  useEffect(() => {
    if (!enableLocalCache) {
      return;
    }
    if (!state.callId) {
      return;
    }
    if (state.callState === 'idle') {
      return;
    }
    localStorage.setItem('call:id', state.callId);
    if (state.callCtx) {
      localStorage.setItem(`call:${state.callId}:ctx`, JSON.stringify(state.callCtx));
    }
    if (state.from) {
      localStorage.setItem(`call:${state.callId}:from`, JSON.stringify(state.from));
    }
    if (state.contact) {
      localStorage.setItem(`call:${state.callId}:contact`, JSON.stringify(state.contact));
    }
    localStorage.setItem(`call:${state.callId}:direction`, state.direction);

    return () => {
      localStorage.removeItem('call:id');
      localStorage.removeItem(`call:${state.callId}:ctx`);
      localStorage.removeItem(`call:${state.callId}:from`);
      localStorage.removeItem(`call:${state.callId}:direction`);
      localStorage.removeItem(`call:${state.callId}:contact`);
    };
  }, [enableLocalCache, state.callId, state.callCtx, state.from, state.direction, state.contact]);

  useEffect(() => {
    if (!enableLocalCache) {
      return;
    }
    const i = setInterval(() => {
      if (!state.callId && state.callState === 'idle') {
        return;
      }
      localStorage.setItem(`call:${state.callId}:timestamp`, Date.now().toString());
    }, 1000);

    return () => {
      clearInterval(i);
      localStorage.removeItem(`call:${state.callId}:timestamp`);
    };
  }, [enableLocalCache, state.callId, state.callState]);

  return <VoiceCallContext.Provider value={reducer}>{children}</VoiceCallContext.Provider>;
};

export const useVoiceCallState = () => {
  const context = use(VoiceCallContext);

  if (!context) {
    throw new Error('useVoiceCallState must be used within a VoiceCallStateProvider');
  }

  return context;
};
